/**
 * Currency Context
 * Provides currency formatting across the entire application
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useOutlet } from './OutletContext';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext(null);

const DEFAULT_CURRENCY = {
  currency_code: 'IDR',
  currency_symbol: 'Rp',
  currency_position: 'before',
  decimal_places: 0,
  decimal_separator: ',',
  thousand_separator: '.',
};

export function CurrencyProvider({ children }) {
  const { api, isAuthenticated } = useAuth();
  const { currentOutlet } = useOutlet();
  const [currencySettings, setCurrencySettings] = useState(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(false);

  const fetchCurrencySettings = useCallback(async () => {
    if (!currentOutlet?.id || !isAuthenticated) return;
    
    setLoading(true);
    try {
      const resp = await api.get('/settings/currency', {
        params: { outlet_id: currentOutlet.id },
      });
      setCurrencySettings(resp.data);
    } catch (error) {
      console.error('Failed to fetch currency settings:', error);
      // Keep default settings on error
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id, isAuthenticated]);

  useEffect(() => {
    fetchCurrencySettings();
  }, [fetchCurrencySettings]);

  /**
   * Format a number as currency based on outlet settings
   * @param {number} amount - The amount to format
   * @param {boolean} showSymbol - Whether to show currency symbol (default: true)
   * @returns {string} Formatted currency string
   */
  const formatCurrency = useCallback((amount, showSymbol = true) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return showSymbol ? `${currencySettings.currency_symbol} 0` : '0';
    }

    const {
      currency_symbol,
      currency_position,
      decimal_places,
      decimal_separator,
      thousand_separator,
    } = currencySettings;

    // Round to decimal places
    const rounded = Number(amount.toFixed(decimal_places));
    
    // Split integer and decimal parts
    const [intPart, decPart] = rounded.toFixed(decimal_places).split('.');
    
    // Add thousand separator
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousand_separator);
    
    // Combine
    let formatted = formattedInt;
    if (decimal_places > 0 && decPart) {
      formatted += decimal_separator + decPart;
    }
    
    // Add currency symbol
    if (!showSymbol) {
      return formatted;
    }
    
    if (currency_position === 'before') {
      return `${currency_symbol} ${formatted}`;
    } else {
      return `${formatted} ${currency_symbol}`;
    }
  }, [currencySettings]);

  /**
   * Parse a formatted currency string back to number
   * @param {string} formattedValue - The formatted currency string
   * @returns {number} The numeric value
   */
  const parseCurrency = useCallback((formattedValue) => {
    if (!formattedValue) return 0;
    
    const { currency_symbol, decimal_separator, thousand_separator } = currencySettings;
    
    // Remove currency symbol and spaces
    let cleaned = formattedValue
      .replace(currency_symbol, '')
      .replace(/\s/g, '')
      .replace(new RegExp(`\\${thousand_separator}`, 'g'), '');
    
    // Replace decimal separator with dot for parsing
    if (decimal_separator !== '.') {
      cleaned = cleaned.replace(decimal_separator, '.');
    }
    
    return parseFloat(cleaned) || 0;
  }, [currencySettings]);

  const value = {
    currencySettings,
    formatCurrency,
    parseCurrency,
    loading,
    refreshCurrency: fetchCurrencySettings,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

export default CurrencyContext;
